import { Form, Input, Space, Table } from 'antd';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_USER } from '../constants/AddUserDefault';
import REGIONS from '../constants/regions';
import TECHNOLOGY_TYPE from '../constants/technologyType';
import AddUserProps from '../interfaces/AddUserProps';
import '../pages/NetworkActivity.css';
import { ApiHandler } from '../services/apiHandler';
import { getAllowedRoles, getOrganisationId, getRole, getTechnology } from '../services/helper';
import './CbsdVendorCards/CbsdVendorCards.css';
import ErrorAlert from './ErrorAlert';
import LabelledInput from './LabelledInput';
import LabelLessDropdown from './LabelLessDropdown';
import Modal from './Modal';
import PrimaryButton from './PrimaryButton';
import SuccessAlert from './SuccessAlert';
import './Users.css';
import WarningAlert from './WarningAlert';
import ConfirmAlert from './confirmAlert';
import { set } from 'rsuite/esm/utils/dateUtils';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';

interface UsersProps {
  processing: any;
}

interface DataType {
  key: any;
  cpi_id: string;
  email: string;
  full_name: string;
  role: string;
  technology_type: string;
  username: string;
  id: number;
  // actions : any
}

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: any;
  inputType: 'text' | 'role' | 'technology';
  record: DataType;
  index: number;
  roleSelection: any[];
  userRole: string;
}

const EditableCell: React.FC<React.PropsWithChildren<EditableCellProps>> = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  roleSelection,
  userRole,
  ...restProps
}) => {
  // const inputNode = inputType === 'number' ? <InputNumber /> : <Input />;

  let inputNode;

  if (inputType === 'role') {
    inputNode = <LabelLessDropdown name={'role'} className="mb-2" fullWidth title={'Role'} options={roleSelection} />;
  } else if (inputType === 'technology') {
    inputNode = (
      <LabelLessDropdown
        disabled={userRole !== 'master admin'}
        name={'technology_type'}
        className="mb-2"
        fullWidth
        options={TECHNOLOGY_TYPE.map((tech: any) => {
          return tech.displayName;
        })}
        title={'Technology'}
      />
    );
  } else {
    inputNode = <Input />;
  }

  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          rules={[
            {
              required: true,
              message: `Please Input ${title}!`,
            },
          ]}>
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

const Users: React.FC<UsersProps> = ({ processing }) => {
  let navigate = useNavigate();
  let technologyTypeName = getTechnology() ?? '';
  let userRole = getRole() || '';
  const [users, setUsers] = useState<any>([]);
  const [newUser, setNewUser] = useState<AddUserProps>(DEFAULT_USER);
  const [modalVisible, toggleModalVisibility] = useState(false);
  const [userCount, setUserCount] = useState(0);
  const [currentUserPage, setCurrentUserPage] = useState(0);
  const [userRowsPerPage, setUserRowsPerPage] = useState(10);
  const [searchString, setSearchString] = useState('');
  const [searchedUsers, setSearchedUsers] = useState<any[]>([]);
  const [showSearchedusers, setShowSearchedUsers] = useState(false);
  const [error, setError] = useState({
    message: '',
    alert: false,
  });
  const [success, setSuccess] = useState({
    message: '',
    alert: false,
  });
  const [warning, setWarning] = useState({
    message: '',
    alert: false,
  });
  const [roleSelection, setRoleSelection] = useState<any[]>([]);
  const [technologyTypeTitle, setTechnologyTypeTitle] = useState(technologyTypeName);
  const [isEdit, edit] = useState(false);
  const [passwordModalVisible, togglePasswordModalVisibility] = useState(false);
  const [deleteConfirmVisible, toggleDeleteConfirmVisibility] = useState(false);
  const [form] = Form.useForm();
  const [editingKey, setEditingKey] = useState('');
  const isEditing = (record: DataType) => record.key === editingKey;
  const [userID, setUserID] = useState<number | undefined>(undefined);
  const [selectedUser, setSelectedUser] = useState<any>(null);

  useEffect(() => {
    roleValidation();
    getUsers();
  }, [currentUserPage, userRowsPerPage]);

  useEffect(() => {
    setNewUser(DEFAULT_USER);
    if ((technologyTypeName === 'cbrs_5g' || technologyTypeName === '5g') && userRole !== 'master admin') {
      setTechnologyTypeTitle('5G');
    } else if (technologyTypeName === 'wifi_6e' && userRole !== 'master admin') {
      setTechnologyTypeTitle('Wi-Fi 6E');
    } else {
      setTechnologyTypeTitle('Technology type *');
    }
  }, [modalVisible]);

  useEffect(() => {
    if (searchString.trim().length > 0) {
      getSearchedUsers(searchString);
      setShowSearchedUsers(true);
    } else {
      setSearchedUsers([]);
      setShowSearchedUsers(false);
      getUsers();
    }
  }, [searchString]);

  const catchApiError = (error: any) => {
    processing(false);
    if (error.doLogout === true) {
      setError({ message: 'Session Timed Out', alert: true });
      setTimeout(() => navigate('/login', { replace: true }), 2000);
    } else {
      setError({ message: error?.data?.message, alert: true });
    }
  };

  const getSearchedUsers = (searchTerm: string) => {
    ApiHandler({
      apiName: 'searchUsers',
      params: {},
      queryParams: { name: searchTerm },
      body: {},
    })
      .then((response: any) => {
        setSearchedUsers(response.data.users);
      })
      .catch((error: any) => {
        catchApiError(error);
      });
  };

  const getUsers = () => {
    processing(true);
    const queryParams: any = {
      page: currentUserPage,
      limit: userRowsPerPage,
    };
    ApiHandler({
      apiName: 'getUsers',
      body: {},
      params: {},
      queryParams: queryParams,
    })
      .then((response: any) => {
        processing(false);
        setUserCount(response.data.count);
        console.log(response, 'response from users');
        setUsers(Array.from(response.data.users).map((item: any, index: number) => ({ ...item, key: index })));
        // setUsers(response.data.users);
      })
      .catch((error: any) => {
        catchApiError(error);
      });
  };

  const handleCreateUserInput = (e: any) => {
    setNewUser((prevValues: any) => {
      return { ...prevValues, [e.target.name]: e.target.value };
    });
  };

  const validateEmail = (email: string): boolean => {
    const validRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
    if (email.match(validRegex)) {
      return true;
    }
    setWarning({ message: 'Invalid Email', alert: true });
    return false;
  };

  const roleValidation = () => {
    if (userRole === 'technology admin') {
      setRoleSelection(getAllowedRoles().slice(1));
    } else if (userRole === 'region admin') {
      setRoleSelection(getAllowedRoles().slice(2));
    } else {
      setRoleSelection(getAllowedRoles());
    }
  };

  const validateNewUser = (): boolean => {
    if (newUser.username.trim() === '') {
      setWarning({ message: 'Username is required', alert: true });
      return false;
    }
    if (newUser.email.trim() === '') {
      setWarning({ message: 'Email is required', alert: true });
      return false;
    }
    if (newUser.role.trim() === '') {
      setWarning({ message: 'Role is required', alert: true });
      return false;
    }
    if (getOrganisationId() !== 1 && newUser.technology_type.trim() === '') {
      setWarning({ message: 'Technology Type is required', alert: true });
      return false;
    }
    if (newUser.role === 'CPI' && newUser.cpi_id.trim() === '') {
      setWarning({
        message: `CPI ID is required for ${newUser.role} role`,
        alert: true,
      });
      return false;
    }
    if (newUser.role === 'CPI' && newUser.cpi_name.trim() === '') {
      setWarning({
        message: `CPI Name is required for ${newUser.role} role`,
        alert: true,
      });
      return false;
    }

    return validateEmail(newUser.email);
  };

  const addUser = () => {
    const isValid: boolean = validateNewUser();
    if (!isValid) return;
    if (userRole.toLowerCase() !== 'master admin')
      newUser.technology_type = TECHNOLOGY_TYPE.filter((tech) => tech.displayName === technologyTypeTitle)[0].dbName;
    if (getOrganisationId() === 1) newUser.technology_type = 'cbrs_5g';
    processing(true);
    ApiHandler({ apiName: 'createUser', body: newUser, params: {} })
      .then((response: any) => {
        processing(false);
        setSuccess({ message: response.data.message, alert: true });
        toggleModalVisibility(false);
        getUsers();
      })
      .catch((error: any) => {
        catchApiError(error);
      });
  };

  const getSelectedUser = (username: string) => {
    // setSearchString(username);
    processing(true);
    ApiHandler({
      apiName: 'searchUser',
      body: {},
      params: { username: username },
    })
      .then((response: any) => {
        processing(false);
        setShowSearchedUsers(false);
        const userFormat: any = [
          {
            cpi_id: response.data.cpi_id,
            email: response.data.email,
            full_name: response.data.full_name,
            role: response.data.role,
            username: response.data.username,
            technology_type: response.data.technology_type,
            key: 0,
          },
        ];
        console.log(userFormat);
        setUsers(userFormat);
        setUserCount(1);
      })
      .catch((error: any) => {
        catchApiError(error);
      });
  };

  const techOptions = [
    { value: 'cbrs_5g', label: '5G(SAS)' },
    { value: 'wifi_6e', label: 'Wi-Fi' },
    { value: 'tarana', label: 'Tarana' },
  ];

  const columns = [
    {
      title: <div className="table-header"> Full Name</div>,
      dataIndex: 'full_name',
      width: '14%',
      editable: true,
      className: 'table-body',
    },
    {
      title: <div className="table-header"> User Name</div>,
      dataIndex: 'username',
      width: '14%',
      editable: false,
      className: 'table-body',
    },
    {
      title: <div className="table-header"> Email</div>,
      dataIndex: 'email',
      width: '20%',
      editable: false,
      className: 'table-body',
    },
    {
      title: <div className="table-header"> Role</div>,
      dataIndex: 'role',
      width: '14%',
      editable: true,
      className: 'table-body',
    },
    {
      title: <div className="table-header"> CPI ID</div>,
      dataIndex: 'cpi_id',
      width: '14%',
      editable: true,
      className: 'table-body',
    },
    {
      title: '',
      width: '5%',
      render: (_: any, record: DataType) => {
        const editable = isEditing(record);
        return (
          <div className="col-1 d-flex justify-content-center">
            {editable ? (
              <div className="row-wise w-100 d-flex align-items-center justify-content-around">
                <Space>
                  {/* <span className="material-icons-round edit-confirm-icon ">done</span> */}
                  <CheckOutlined
                    title="Confirm"
                    onClick={() => {
                      editConfirmed(record);
                    }}
                    className="p-2"
                    style={{ color: 'rgb(22, 119, 255)' }}
                  />
                  <CloseOutlined
                    title="Cancel"
                    onClick={() => {
                      edit(false);
                      setEditingKey('');
                    }}
                    className="p-2"
                    style={{ color: 'rgb(209, 26, 42)' }}
                  />
                  {/* <span className="material-icons-round cancel-confirm-icon">close</span> */}
                </Space>
                {/* <span
                  className="cancel-edit"
                  onClick={() => {
                    edit(false);
                    setEditingKey('');
                  }}>
                  Cancel
                </span> */}
              </div>
            ) : (
              <div className="dropstart">
                <a href="#" role="button" id="dropdownMenuLink" data-bs-toggle="dropdown" aria-expanded="false">
                  <span className="material-icons-round" style={{ color: '#8f9fac' }}>
                    menu_open
                  </span>
                </a>
                <ul className="dropdown-menu" aria-labelledby="dropdownMenuLink">
                  <li
                    className="dropdown-item table-body"
                    onClick={() => {
                      edit(true);
                      editRow(record);
                    }}>
                    Edit
                  </li>
                  <li
                    className="dropdown-item table-body"
                    onClick={() => {
                      toggleDeleteConfirmVisibility(true);
                      setSelectedUser(record);
                    }}>
                    Delete
                  </li>
                  <li
                    className="dropdown-item table-body"
                    onClick={() => {
                      togglePasswordModalVisibility(true);
                    }}>
                    Reset Password
                  </li>
                </ul>
              </div>
            )}
          </div>
        );
      },
      className: 'table-body',
    },
  ];

  const editConfirmed = async (record: DataType) => {
    processing(true);
    const row = (await form.validateFields()) as DataType;
    console.log('row', row);
    if (row) {
      const payload = {
        ...row,
        id: userID,
        technology_type: TECHNOLOGY_TYPE.find((t) => t.displayName === row.technology_type)?.dbName,
      };
      console.log('payload', payload);
      ApiHandler({ apiName: 'updateUser', body: payload, params: {} })
        .then((response: any) => {
          processing(false);
          edit(false);
          setSuccess({ message: response.data.message, alert: true });
          getUsers();
          setEditingKey('');
        })
        .catch((error: any) => {
          processing(false);
          if (error.doLogout === true) {
            setError({ message: 'Session Timed Out', alert: true });
            setTimeout(() => navigate('/login', { replace: true }), 2000);
          } else {
            setError({ message: error.data.message, alert: true });
          }
        });
    }
  };

  const editRow = (record: Partial<DataType> & { key: React.Key }) => {
    form.setFieldsValue({ ...record });
    setEditingKey(record.key);
    setUserID(record.id);
  };

  const mergedColumns: any = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: DataType) => ({
        record,
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
        inputType: col.dataIndex === 'role' ? 'role' : col.dataIndex === 'technology_type' ? 'technology' : 'text',
        roleSelection,
        userRole,
      }),
    };
  });

  const deleteUser = () => {
    toggleDeleteConfirmVisibility(false);
    processing(true);
    ApiHandler({
      apiName: 'deleteUser',
      body: {},
      params: { username: selectedUser.username },
    })
      .then((response: any) => {
        processing(false);
        setSuccess({ message: response.data.message, alert: true });
        getUsers();
      })
      .catch((error: any) => {
        processing(false);
        if (error.doLogout === true) {
          setError({ message: 'Session Timed Out', alert: true });
          setTimeout(() => navigate('/login', { replace: true }), 2000);
        } else {
          setError({ message: error.data.message, alert: true });
        }
      });
  };

  return (
    <div className="w-100">
      <div className="app-card-white px-3 py-2 w-100 position-relative">
        <div className="row-wise space-between">
          <span className="vendor-title w-100 text-color">USERS</span>
          <button
            className="add-span"
            onClick={(e: any) => {
              e.preventDefault();
              toggleModalVisibility(!modalVisible);
            }}>
            ADD
            <span className="material-icons-round font-14">add</span>
          </button>
        </div>

        {/* <div className="row-wise justify-content-between align-items-center mt-2 mb-2">
          <div className='w-75'> */}
        {/* Search Input */}
        {/*  <div className="input-group net-action-box d-flex align-items-center w-100 searchbar h-btn">
              <span className="network-icon material-icons-round ms-2 ">search</span>
              <input
                id="searchString"
                name="searchString"
                type={'text'}
                className={'net-search-input'}
                placeholder={'Search by name'}
                value={searchString}
                onChange={(e: any) => {
                  setSearchString(e.target.value);
                }}
              />
              {searchString !== '' ? (
                <span
                  className="network-icon material-icons-round me-2 m-auto"
                  onClick={() => {
                    setSearchString('');
                  }}>
                  close
                </span>
              ) : (
                <div className="network-icon me-4"></div>
              )}
            </div>

            {showSearchedusers && searchedUsers.length ? (
              <div className="user-search">
                {searchedUsers.map((user: any) => {
                  return (
                    <div
                      key={user.username}
                      className="col-wise pt-2 pb-2 user-search-row"
                      onClick={() => {
                        getSelectedUser(user.username);
                      }}>
                      <span>{user.full_name}</span>
                      <div className="row mt-1">
                        <div className="col row-wise">
                          <span className="user-search-title">Username: </span>
                          <span className="ms-1">{user.username}</span>
                        </div>
                        <div className="col row-wise">
                          <span className="user-search-title">Role: </span>
                          <span className="ms-1">{user.role}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div></div>
            )}
          </div>

          <TablePagination
            component="div"
            count={userCount}
            page={currentUserPage}
            onPageChange={(e: any, newPage: any) => {
              setCurrentUserPage(newPage);
            }}
            rowsPerPage={userRowsPerPage}
            onRowsPerPageChange={(event: any) => {
              setUserRowsPerPage(event.target.value);
            }}
          />
        </div> */}

        {/* <table className="w-100 d-table">
          <thead className="d-table w-100">
            <tr>
              <UsersTableHeader />
            </tr>
          </thead>
          {userCount === 0 ? (
            <div className="center mt-4">
              <span className="empty-data-label">~ Nothing to show ~</span>
            </div>
          ) : (
            <tbody className="user-tablebody">
              {users.map((user: any) => {
                return (
                  <tr key={user.username}>
                    <UserActions
                      user={user}
                      processing={processing}
                      refreshUsers={() => {
                        getUsers();
                      }}
                    />
                  </tr>
                );
              })}
            </tbody>
          )}
        </table> */}
        {/* Search Input */}
        <div
          style={{ marginTop: '45px', zIndex: 10 }}
          className="input-group net-action-box d-flex align-items-center w-100 searchbar h-btn position-absolute">
          <span className="network-icon material-icons-round ms-2 ">search</span>
          <input
            id="searchString"
            name="searchString"
            type={'text'}
            className={'net-search-input'}
            placeholder={'Search by name'}
            value={searchString}
            onChange={(e: any) => {
              setSearchString(e.target.value);
            }}
          />
          {searchString !== '' ? (
            <span
              className="network-icon material-icons-round me-2 m-auto"
              onClick={() => {
                setSearchString('');
              }}>
              close
            </span>
          ) : (
            <div className="network-icon me-4"></div>
          )}
        </div>

        {showSearchedusers && searchedUsers.length ? (
          <div className="user-search">
            {searchedUsers.map((user: any) => {
              return (
                <div
                  key={user.username}
                  className="col-wise pt-2 pb-10 user-search-row"
                  onClick={() => {
                    getSelectedUser(user.username);
                  }}>
                  <span>{user.full_name}</span>
                  <div className="row mt-1">
                    <div className="col row-wise">
                      <span className="user-search-title">Username: </span>
                      <span className="ms-1">{user.username}</span>
                    </div>
                    <div className="col row-wise">
                      <span className="user-search-title">Role: </span>
                      <span className="ms-1">{user.role}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div></div>
        )}
        <Form form={form} component={false}>
          <Table<DataType>
            components={{
              body: { cell: EditableCell },
            }}
            rowClassName="editable-row"
            columns={mergedColumns}
            dataSource={users}
            pagination={{ position: ['topRight'] }}
          />
        </Form>
      </div>

      <Modal show={modalVisible}>
        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content app-card border-0 p-0">
            <div className="modal-header">
              <h5 className="modal-title">Add User</h5>
              <button
                type="button"
                className="btn-close"
                onClick={(e: any) => {
                  e.preventDefault();
                  toggleModalVisibility(!modalVisible);
                }}></button>
            </div>
            <div className="modal-body">
              <LabelledInput
                name="full_name"
                value={newUser.full_name}
                title={'First Name | Last Name'}
                onChange={(e: any) => {
                  handleCreateUserInput(e);
                }}
              />
              <LabelledInput
                name="username"
                value={newUser.username}
                title={'Username *'}
                onChange={(e: any) => {
                  handleCreateUserInput(e);
                }}
              />
              <LabelledInput
                name="email"
                value={newUser.email}
                title={'Email *'}
                onChange={(e: any) => {
                  handleCreateUserInput(e);
                }}
              />
              <LabelLessDropdown
                name={'role'}
                className="mb-2"
                fullWidth
                title={'Role *'}
                options={roleSelection}
                value={newUser.role}
                onChange={(e: any) => {
                  handleCreateUserInput(e);
                }}
              />
              {getOrganisationId() !== 1 ? (
                <LabelLessDropdown
                  disabled={userRole !== 'master admin'}
                  name={'technology_type'}
                  className="mb-2"
                  fullWidth
                  title={technologyTypeTitle}
                  options={TECHNOLOGY_TYPE.map((tech: any) => {
                    return tech.displayName;
                  })}
                  value={TECHNOLOGY_TYPE.filter((tech) => tech.dbName === newUser.technology_type)[0]?.displayName}
                  onChange={(e: any) => {
                    e.target.value = TECHNOLOGY_TYPE.filter((tech) => tech.displayName === e.target.value)[0].dbName;
                    handleCreateUserInput(e);
                  }}
                />
              ) : (
                <></>
              )}

              <LabelLessDropdown
                name={'region'}
                className="mb-2"
                fullWidth
                title={'Region'}
                options={REGIONS}
                value={newUser.region}
                onChange={(e: any) => {
                  handleCreateUserInput(e);
                }}
              />
              <LabelledInput
                name="cpi_id"
                value={newUser.cpi_id}
                title={'CPI ID*'}
                onChange={(e: any) => {
                  handleCreateUserInput(e);
                }}
              />
              <LabelledInput
                name="cpi_name"
                value={newUser.cpi_name}
                title={'CPI Name*'}
                onChange={(e: any) => {
                  handleCreateUserInput(e);
                }}
              />
            </div>
            <div className="modal-footer">
              <PrimaryButton label="Create" disable={false} clicked={addUser} />
            </div>
          </div>
        </div>
      </Modal>

      <ConfirmAlert
        title="CONFIRM DELETE"
        message="Do you want to delete this user ?"
        // note="The field installer will arrive at this location to install remote nodes."
        primaryText="Confirm"
        secondaryText="Cancel"
        visible={deleteConfirmVisible}
        onClose={() => toggleDeleteConfirmVisibility(false)}
        secondaryAction={() => toggleDeleteConfirmVisibility(false)}
        primaryAction={deleteUser}
      />
      <ErrorAlert
        show={error.alert}
        onDismiss={() => {
          setError({ message: '', alert: false });
        }}
        message={error.message}
      />
      <SuccessAlert
        show={success.alert}
        onDismiss={() => {
          setSuccess({ message: '', alert: false });
        }}
        message={success.message}
      />
      <WarningAlert
        show={warning.alert}
        onDismiss={() => {
          setWarning({ message: '', alert: false });
        }}
        message={warning.message}
      />
    </div>
  );
};

export default Users;
